import { Dialog, Transition } from '@headlessui/react'
import { Xmark } from 'iconoir-react'
import { Fragment } from 'react'
import Button from './Button'

export interface ConfirmationOptions {
  variant: `danger` | `info`
  title: string
  description: string
  submitLabel?: string
  cancelLabel?: string
}

interface ConfirmationAlertProps extends ConfirmationOptions {
  open: boolean
  onSubmit: () => void
  onClose: () => void
  onExited: () => void
}

export default function ConfirmationAlert({
  open,
  onSubmit,
  onClose,
  onExited,
  variant,
  title,
  description,
  submitLabel = `Confirm`,
  cancelLabel = `Cancel`,
}: ConfirmationAlertProps) {
  return (
    <Transition.Root show={open} as={Fragment} afterLeave={onExited}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-75"
          leave="ease-in duration-200"
          leaveFrom="opacity-75"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-elevation-4 transition-all sm:w-full sm:max-w-lg">
                <div className="flex h-[56px] items-center justify-between border-b border-black/10 px-6 py-3">
                  <Dialog.Title as="h3" className="text-lg font-semibold leading-tight text-trn-900">
                    {title}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-trn-900">
                    <Xmark className="h-6 w-6" />
                  </button>
                </div>
                <div className="px-6 py-4">
                  <p className="whitespace-pre-wrap text-body-xs text-trn-700">{description}</p>
                </div>
                <div className="flex justify-end gap-4 border-t border-black/10 px-6 py-3">
                  <Button label={cancelLabel} mode="white" size="md" onClick={onClose} className="!px-6 !py-3" />
                  <Button
                    type="button"
                    label={submitLabel}
                    mode={variant === `danger` ? `danger` : `primary`}
                    size="md"
                    onClick={onSubmit}
                    className="!px-6 !py-3"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
